import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, getFileUrl } from "../../../api/apiClient";
import "../../../assets/css/admin/contents/ContentList.css";
import "../../../assets/css/admin/AdminCommon.css";

import SearchD from "../../../assets/img/Search_D.svg";
import SearchF from "../../../assets/img/Search_F.svg";
import flowerIcon from "../../../assets/img/flower_icon.png";

function CalendarAdmin() {
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [calendarData, setCalendarData] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const yearOptions = Array.from({ length: 7 }, (_, index) => {
    return now.getFullYear() - 2 + index;
  });

  const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);

  const CalendarLabel = ({ children }) => {
    return (
      <label className="calendar-upload-label">
        <img src={flowerIcon} alt="" />
        <span>{children}</span>
      </label>
    );
  };

  const fetchCalendar = async () => {
    try {
      setLoading(true);

      const result = await apiClient(`/api/activity/calendar/${year}/${month}`, {
        method: "GET",
      });

      console.log("달력 조회 성공:", result);

      const data = result?.data || result;
      setCalendarData(data);

      if (data?.imageUrl) {
        setPreviewUrl(getFileUrl(data.imageUrl));
      } else {
        setPreviewUrl("");
      }

      setSelectedImage(null);
      setSelectedFileName("");
    } catch (error) {
      console.error("달력 조회 실패:", error);

      setCalendarData(null);
      setPreviewUrl("");
      setSelectedImage(null);
      setSelectedFileName("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  const handlePrevMonth = () => {
    if (month === 1) {
      setYear((prev) => prev - 1);
      setMonth(12);
      return;
    }

    setMonth((prev) => prev - 1);
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setYear((prev) => prev + 1);
      setMonth(1);
      return;
    }

    setMonth((prev) => prev + 1);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("이미지 파일만 업로드할 수 있습니다.");
      e.target.value = "";
      return;
    }

    setSelectedImage(file);
    setSelectedFileName(file.name);
    setPreviewUrl(URL.createObjectURL(file));

    e.target.value = "";
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setSelectedFileName("");

    if (calendarData?.imageUrl) {
      setPreviewUrl(getFileUrl(calendarData.imageUrl));
    } else {
      setPreviewUrl("");
    }
  };

  const handleSave = async () => {
    if (!selectedImage) {
      alert("업로드할 달력 이미지를 선택해 주세요.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("imageFile", selectedImage);

      await apiClient(`/api/activity/calendar?year=${year}&month=${month}`, {
        method: "POST",
        body: formData,
      });

      alert("달력 이미지가 저장되었습니다.");

      await fetchCalendar();
    } catch (error) {
      console.error("달력 저장 오류:", error);
      alert(error.message || "달력 저장 중 오류가 발생했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content-list-page admin-scroll-x">
      <div className="content-list-inner">
        <h2 className="admin-title">달력 관리</h2>

        <div className="calendar-manage-page">
          <div className="calendar-month-control">
            <button
              type="button"
              className="calendar-arrow-btn"
              onClick={handlePrevMonth}
              disabled={loading || saving}
            >
              ‹
            </button>

            <div className="calendar-select-wrap calendar-year-wrap">
              <select
                className="calendar-select calendar-year-select"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                disabled={loading || saving}
              >
                {yearOptions.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>
                    {yearOption}년
                  </option>
                ))}
              </select>

              <span className="calendar-select-arrow" aria-hidden="true" />
            </div>

            <div className="calendar-select-wrap calendar-month-wrap">
              <select
                className="calendar-select calendar-month-select"
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                disabled={loading || saving}
              >
                {monthOptions.map((monthOption) => (
                  <option key={monthOption} value={monthOption}>
                    {monthOption}월
                  </option>
                ))}
              </select>

              <span className="calendar-select-arrow" aria-hidden="true" />
            </div>

            <button
              type="button"
              className="calendar-arrow-btn"
              onClick={handleNextMonth}
              disabled={loading || saving}
            >
              ›
            </button>
          </div>

          <div className="calendar-upload-row">
            <CalendarLabel>달력 이미지</CalendarLabel>

            <input
              className="calendar-upload-input"
              type="text"
              placeholder="달력 이미지를 업로드해 주세요."
              value={
                selectedFileName ||
                calendarData?.originalFileName ||
                (calendarData?.imageUrl ? "기존 달력 이미지" : "")
              }
              readOnly
            />

            <label className="calendar-upload-btn">
              파일 선택
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
                disabled={saving}
              />
            </label>
          </div>

          {previewUrl && (
            <div className="calendar-preview-row">
              <CalendarLabel>미리보기</CalendarLabel>

              <div className="calendar-preview-box">
                <img src={previewUrl} alt={`${year}년 ${month}월 달력`} />
              </div>
            </div>
          )}

          <div className="calendar-button-row">
            <button
              type="button"
              className="calendar-cancel-btn"
              onClick={handleCancel}
              disabled={saving || loading}
            >
              취소
            </button>

            <button
              type="button"
              className="calendar-save-btn"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentList() {
  const navigate = useNavigate();
  const { category = "notice" } = useParams();

  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchType, setSearchType] = useState("title");
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const searchIcon = searchKeyword.trim() ? SearchF : SearchD;

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return String(value).slice(0, 10).replaceAll("-", ".");
    }

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    return `${yyyy}.${mm}.${dd}`;
  };

  const getTimeValue = (value) => {
    if (!value) return 0;

    const date = new Date(value);
    const time = date.getTime();

    return Number.isNaN(time) ? 0 : time;
  };

  const getPagedArray = (result) => {
    const data = result?.data;

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.contents)) return data.contents;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.list)) return data.list;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.data?.content)) return data.data.content;

    return [];
  };

  const getListUrl = () => {
    if (category === "notice") {
      return "/api/activity/notice?searchCategory=TITLE&page=0&size=100&sort=createdAt,desc";
    }

    if (category === "event") {
      return "/api/activity/event?page=0&size=100&sort=createdAt,desc";
    }

    if (category === "partner") {
      return "/api/activity/partnership?listCategory=ALL&page=0&size=100&sort=createdAt,desc";
    }

    return null;
  };

  const getDetailUrl = (id) => {
    if (category === "notice") return `/api/activity/notice/${id}`;
    if (category === "event") return `/api/activity/event/${id}`;
    if (category === "partner") return `/api/activity/partnership/${id}`;

    return null;
  };

  const getDetailContent = async (id) => {
    const url = getDetailUrl(id);

    if (!url) return "";

    try {
      const result = await apiClient(url);
      const data = result?.data || result;

      return (
        data?.content ||
        data?.description ||
        data?.body ||
        data?.benefitSummary ||
        data?.mainContent ||
        ""
      );
    } catch (error) {
      console.error(`${category} 상세 내용 조회 실패:`, error);
      return "";
    }
  };

  const normalizeItem = (item) => {
    if (category === "notice") {
      const id = item.noticeId ?? item.id;
      const createdAtRaw = item.createdAt || "";

      return {
        id,
        title: item.title ?? "",
        writer: item.createdBy ?? "관리자",
        date: formatDate(createdAtRaw),
        createdAtRaw,
        content: item.content ?? "",
      };
    }

    if (category === "event") {
      const id = item.eventId ?? item.id;
      const createdAtRaw = item.createdAt || item.startDate || "";

      return {
        id,
        title: item.title || item.eventName || "",
        writer: item.createdBy ?? "관리자",
        date: formatDate(createdAtRaw),
        createdAtRaw,
        content: item.content || item.description || "",
        startDate: item.startDate || "",
        endDate: item.endDate || "",
        mainImg: item.mainImg || "",
      };
    }

    if (category === "partner") {
      const id = item.partnershipId ?? item.id;
      const createdAtRaw = item.createdAt || item.startDate || "";

      return {
        id,
        title: item.title || item.companyName || "",
        writer: item.createdBy ?? "관리자",
        date: formatDate(createdAtRaw),
        createdAtRaw,
        content: item.content || item.benefitSummary || "",
        companyName: item.companyName || "",
        category: item.category || "",
        durationType: item.durationType || "",
        startDate: item.startDate || "",
        endDate: item.endDate || "",
        mainImg: item.mainImg || "",
      };
    }

    return item;
  };

  const fetchContentList = async () => {
    const url = getListUrl();

    if (!url) {
      setContentList([]);
      return;
    }

    try {
      setLoading(true);

      const result = await apiClient(url);
      console.log(`${category} 목록 응답:`, result);

      const list = await Promise.all(
        getPagedArray(result).map(async (item) => {
          const normalized = normalizeItem(item);

          if (!normalized.content && normalized.id) {
            const detailContent = await getDetailContent(normalized.id);

            return {
              ...normalized,
              content: detailContent,
            };
          }

          return normalized;
        })
      );

      setContentList(list);
    } catch (error) {
      console.error(`${category} 목록 조회 오류:`, error);
      alert(error.message || "목록 조회 중 오류가 발생했습니다.");
      setContentList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (category === "calendar") return;

    setSearchType("title");
    setFilterOpen(false);
    setSearchKeyword("");
    setCurrentPage(1);

    fetchContentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const getTitle = () => {
    if (category === "notice") return "공지사항";
    if (category === "event") return "행사";
    if (category === "partner") return "제휴·협력";
    return "";
  };

  const getSearchLabel = () => {
    if (searchType === "title") return "제목";
    if (searchType === "content") return "내용";
    return "제목";
  };

  const currentList = useMemo(() => {
    const keyword = searchKeyword.trim();

    const filtered = contentList.filter((item) => {
      if (!keyword) return true;

      if (searchType === "content") {
        return (item.content || "").includes(keyword);
      }

      return (item.title || "").includes(keyword);
    });

    const sorted = [...filtered];

    sorted.sort((a, b) => {
      const timeA = getTimeValue(a.createdAtRaw);
      const timeB = getTimeValue(b.createdAtRaw);

      if (timeA !== timeB) {
        return timeB - timeA;
      }

      return Number(b.id || 0) - Number(a.id || 0);
    });

    return sorted;
  }, [contentList, searchKeyword, searchType]);

  const totalPages = Math.max(1, Math.ceil(currentList.length / PAGE_SIZE));

  const pagedList = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return currentList.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentList, currentPage]);

  const pageGroupStart = Math.floor((currentPage - 1) / 5) * 5 + 1;
  const pageGroupEnd = Math.min(pageGroupStart + 4, totalPages);

  const pageNumbers = Array.from(
    { length: pageGroupEnd - pageGroupStart + 1 },
    (_, index) => pageGroupStart + index
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const getDeleteUrl = (id) => {
    if (category === "notice") return `/api/activity/notice/${id}`;
    if (category === "event") return `/api/activity/event/${id}`;
    if (category === "partner") return `/api/activity/partnership/${id}`;
    return null;
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("삭제하시겠습니까?");
    if (!confirmDelete) return;

    const url = getDeleteUrl(id);

    if (!url) {
      alert("삭제 API가 연결되어 있지 않습니다.");
      return;
    }

    try {
      await apiClient(url, {
        method: "DELETE",
      });

      setContentList((prev) => prev.filter((item) => item.id !== id));
      alert("삭제되었습니다.");
    } catch (error) {
      console.error("삭제 오류:", error);
      alert(error.message || "삭제 중 오류가 발생했습니다.");
    }
  };

  const handleWriteMove = () => {
    navigate("write");
  };

  const handleEditMove = (item) => {
    navigate(`edit/${item.id}`);
  };

  const handleSelectFilter = (value) => {
    setSearchType(value);
    setFilterOpen(false);
    setCurrentPage(1);
  };

  if (category === "calendar") {
    return <CalendarAdmin />;
  }

  return (
    <div className="content-list-page admin-scroll-x">
      <div className="content-list-inner">
        <h2 className="admin-title">{getTitle()} 관리</h2>

        <div className="content-admin-toolbar">
          <button className="add-btn" onClick={handleWriteMove} type="button">
            +
          </button>

          <div className="content-admin-filter">
            <div className="custom-filter">
              <button
                type="button"
                className={`filter-toggle ${filterOpen ? "is-open" : ""}`}
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <span>{getSearchLabel()}</span>
                <span className="filter-arrow" aria-hidden="true" />
              </button>

              {filterOpen && (
                <div className="filter-menu">
                  <button
                    type="button"
                    onClick={() => handleSelectFilter("title")}
                  >
                    제목
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelectFilter("content")}
                  >
                    내용
                  </button>
                </div>
              )}
            </div>

            <div className="admin-search-box">
              <input
                type="text"
                placeholder="검색어를 입력하세요."
                value={searchKeyword}
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                  setCurrentPage(1);
                }}
              />

              <button
                type="button"
                className="admin-search-btn"
                onClick={() => setCurrentPage(1)}
              >
                <img src={searchIcon} alt="검색" />
              </button>
            </div>
          </div>
        </div>

        <div className="table-wrap">
          <div className="table-header">
            <div>No</div>
            <div>제목</div>
            <div>작성자</div>
            <div>작성일</div>
            <div>관리</div>
          </div>

          {loading ? (
            <div className="table-empty-row">불러오는 중입니다.</div>
          ) : pagedList.length > 0 ? (
            pagedList.map((item, index) => {
              const displayNo = String(
                currentList.length - ((currentPage - 1) * PAGE_SIZE + index)
              ).padStart(3, "0");

              return (
                <div key={item.id} className="table-row">
                  <div>{displayNo}</div>

                  <div>{item.title}</div>
                  <div>{item.writer}</div>
                  <div>{item.date}</div>

                  <div className="table-actions">
                    <button
                      className="edit"
                      onClick={() => handleEditMove(item)}
                      type="button"
                    >
                      수정
                    </button>

                    <button
                      className="delete"
                      onClick={() => handleDelete(item.id)}
                      type="button"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="table-empty-row">등록된 게시글이 없습니다.</div>
          )}
        </div>

        <div className="pagination">
          <button
            type="button"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1 || currentList.length === 0}
          >
            «
          </button>

          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1 || currentList.length === 0}
          >
            ‹
          </button>

          {pageNumbers.map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              className={currentPage === pageNumber ? "active" : ""}
              onClick={() => setCurrentPage(pageNumber)}
              disabled={currentList.length === 0}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages || currentList.length === 0}
          >
            ›
          </button>

          <button
            type="button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages || currentList.length === 0}
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContentList;